// ── V162: 중국어로만 적힌 단가표 품목 → 한국어 이름 자동 ─────────────────────────────
// 사장님: "입고에서 중국어로 입력되었을 때 한글 입력을 자동으로 해 주라 했는데 적용 안 된 듯.
//          입고·단가표 등에서 모두 중국어 입력 시 한글 변환 입력도 자동으로"
//   (P258 '红盛绿槽竹碳' — 상호가 붙은 이름이라 번역 API가 한국어를 못 돌려줬고, 그대로 중국어 이름으로 저장됐다)
// 입고·단가표에서 저장할 때 번역이 실패해도, 앱이 뒤에서 다시 번역해 한국어 이름을 채운다 (App.js).
//   name = 한국어, name_zh = 원래 적은 중국어. 레시피 식재료 이름도 같이 바꿔서 연결이 끊기지 않게.
import { hasHangul, hasCjk } from './koZh';
import { sameIngredientName } from './ingredientMatch';
import { translateToKo } from './translate';
import { zhToHangul } from '../i18n/pinyinHangul';

const s = (x) => String(x == null ? '' : x).trim();

/**
 * 중국어 품목 이름 → 한국어 이름. 번역(용어집·캐시·AI)이 안 되면 병음을 한글로 (红盛绿槽竹碳 → 훙성뤼차오주탄)
 * 사장님: "한국어로 번역이 안 되면 그냥 병음을 한국어로 적어 주면 될 것 같아"
 */
export async function koNameOrSound(zh, ms = 5000) {
  const raw = s(zh);
  if (!raw) return '';
  if (hasHangul(raw)) return raw;
  let ko = '';
  try { ko = s(await Promise.race([translateToKo(raw, 'item'), new Promise(r => setTimeout(() => r(null), ms))])); } catch (e) { ko = ''; }
  if (ko && hasHangul(ko)) return ko;
  try { return s(await zhToHangul(raw)); } catch (e) { return ''; }
}

/** 한국어 이름이 없는 단가표 줄 (중국어로만 적힘, 반제품 제외 — 반제품 이름은 레시피가 정한다) */
export const needsKoName = (p) => !!p && p.category !== '반제품' && hasCjk(p.name) && !hasHangul(p.name);

/** 번역할 이름들 — 같은 이름은 한 번, tries(이름 → 시도 횟수)가 maxTries보다 적은 것만 */
export function koNameCandidates(prices = [], tries = new Map(), maxTries = 3) {
  const out = [];
  for (const p of prices || []) {
    if (!needsKoName(p)) continue;
    const n = s(p.name);
    if (out.includes(n) || (tries.get(n) || 0) >= maxTries) continue;
    out.push(n);
  }
  return out;
}

/**
 * 번역 결과(map: 중국어 → 한국어)를 단가표·레시피에 반영
 * @returns {{ prices, categories, renamed: [중국어, 한국어][], touched: 바뀐 레시피 식재료 수 }}
 */
export function applyKoNames(prices = [], categories = [], map = new Map()) {
  const renamed = [];
  const next = (prices || []).map(p => {
    if (!needsKoName(p)) return p;
    const zh = s(p.name);
    const ko = s(map.get(zh));
    if (!ko || !hasHangul(ko)) return p;
    const curZh = s(p.name_zh);
    if (!renamed.some(([z]) => z === zh)) renamed.push([zh, ko]);
    // 중국어 칸에 다른 중국어가 따로 적혀 있으면 그대로, 아니면 원래 적은 중국어
    return { ...p, name: ko, name_zh: curZh && curZh !== zh && !hasHangul(curZh) ? curZh : zh };
  });
  let touched = 0;
  let cats = categories;
  if (renamed.length) {
    cats = (categories || []).map(c => {
      let cc = false;
      const items = (c.items || []).map(item => {
        if (!Array.isArray(item?.ingredients)) return item;
        let ch = false;
        const ings = item.ingredients.map(ing => {
          const hit = renamed.find(([z]) => sameIngredientName(ing?.name, z));
          if (!hit) return ing;
          ch = true; touched += 1;
          return { ...ing, name: hit[1] };
        });
        if (!ch) return item;
        cc = true;
        return { ...item, ingredients: ings };
      });
      return cc ? { ...c, items } : c;
    });
  }
  return { prices: next, categories: cats, renamed, touched };
}
